// UserZones - Clean Modern Design

import { useEffect, useState, memo, useMemo, useCallback } from 'react';
import { ZoneMap } from "./ZonesMap";
import { navigate, safeFetch } from "./App";
import { motion, AnimatePresence } from 'framer-motion';
import { cacheSafeFetch } from "./cacheSafeFetch";
import { MapPin, Users, ChevronLeft, ChevronDown, ChevronUp, Search, Plus } from "lucide-react";

// Color schemes for zone cards (matching homepage)
const colorSchemes = [
    {
        bg: 'from-info-50/60 via-brand-50/40 to-white',
        border: 'border-info-100/50 hover:border-info-500/60',
        accent: 'from-info-500 via-brand-500 to-brand-600',
        icon: 'from-info-500 via-brand-600 to-brand-600',
        iconHover: 'group-hover:shadow-info-100/50',
        badge: 'from-info-50/80 to-brand-50/70 border-info-100/40',
        badgeText: 'text-info-700',
        badgeIcon: 'text-brand-600',
        avatarBg: 'from-info-100/40 to-brand-200/40',
        avatarText: 'text-info-700',
        counter: 'text-info-700 bg-info-50 border-info-100/50',
        arrow: 'text-info-500/60 group-hover:text-info-600',
        titleHover: 'group-hover:text-brand-600'
    },
    {
        bg: 'from-success-50/60 via-success-50/40 to-white',
        border: 'border-success-100/50 hover:border-success-500/60',
        accent: 'from-success-500 via-success-500 to-info-600',
        icon: 'from-success-500 via-success-600 to-info-600',
        iconHover: 'group-hover:shadow-success-100/50',
        badge: 'from-success-50/80 to-success-50/70 border-success-100/40',
        badgeText: 'text-success-700',
        badgeIcon: 'text-success-600',
        avatarBg: 'from-success-100/40 to-success-100/40',
        avatarText: 'text-success-700',
        counter: 'text-success-700 bg-success-50 border-success-100/50',
        arrow: 'text-success-500/60 group-hover:text-success-600',
        titleHover: 'group-hover:text-success-600'
    },
    {
        bg: 'from-secondary-50/60 via-secondary-50/40 to-white',
        border: 'border-secondary-200/50 hover:border-secondary-400/60',
        accent: 'from-secondary-400 via-secondary-500 to-brand-600',
        icon: 'from-secondary-500 via-secondary-600 to-brand-600',
        iconHover: 'group-hover:shadow-secondary-300/50',
        badge: 'from-secondary-50/80 to-secondary-50/70 border-secondary-200/40',
        badgeText: 'text-secondary-700',
        badgeIcon: 'text-secondary-600',
        avatarBg: 'from-secondary-200/40 to-secondary-200/40',
        avatarText: 'text-secondary-700',
        counter: 'text-secondary-700 bg-secondary-50 border-secondary-200/50',
        arrow: 'text-secondary-400/60 group-hover:text-secondary-600',
        titleHover: 'group-hover:text-secondary-600'
    },
    {
        bg: 'from-warning-50/60 via-warning-50/40 to-white',
        border: 'border-warning-100/50 hover:border-warning-500/60',
        accent: 'from-warning-500 via-warning-500 to-danger-600',
        icon: 'from-warning-500 via-warning-600 to-danger-600',
        iconHover: 'group-hover:shadow-warning-100/50',
        badge: 'from-warning-50/80 to-warning-50/70 border-warning-100/40',
        badgeText: 'text-warning-700',
        badgeIcon: 'text-warning-600',
        avatarBg: 'from-warning-100/40 to-warning-100/40',
        avatarText: 'text-warning-700',
        counter: 'text-warning-700 bg-warning-50 border-warning-100/50',
        arrow: 'text-warning-500/60 group-hover:text-warning-600',
        titleHover: 'group-hover:text-warning-600'
    },
    {
        bg: 'from-brand-50/60 via-brand-50/40 to-white',
        border: 'border-brand-200/50 hover:border-brand-400/60',
        accent: 'from-brand-400 via-brand-500 to-brand-600',
        icon: 'from-brand-500 via-brand-600 to-brand-600',
        iconHover: 'group-hover:shadow-brand-300/50',
        badge: 'from-brand-50/80 to-brand-50/70 border-brand-200/40',
        badgeText: 'text-brand-700',
        badgeIcon: 'text-brand-600',
        avatarBg: 'from-brand-200/40 to-brand-200/40',
        avatarText: 'text-brand-700',
        counter: 'text-brand-700 bg-brand-50 border-brand-200/50',
        arrow: 'text-brand-400/60 group-hover:text-brand-600',
        titleHover: 'group-hover:text-brand-600'
    }
];

// Zone Card Component
const ZoneCard = memo(({ zone, index, onClick, onRemove, memberCount, members, isMember }) => {
    const colors = colorSchemes[index % colorSchemes.length];

    return (
        <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.05, duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            whileHover={{ scale: 1.01, transition: { duration: 0.15 } }}
        >
            <div
                onClick={onClick}
                className={`group relative bg-gradient-to-l ${colors.bg} rounded-xl p-4 cursor-pointer transition-all duration-200 border ${colors.border} shadow-sm hover:shadow-lg overflow-hidden`}
            >
                {/* Animated gradient overlay on hover */}
                <div className={`absolute inset-0 bg-gradient-to-l from-${colors.bg.split('-')[1]}100/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />

                {/* Accent bar with vibrant gradient */}
                <div className={`absolute right-0 top-0 bottom-0 w-1 bg-gradient-to-b ${colors.accent} opacity-70 group-hover:opacity-100 transition-opacity duration-300 shadow-md`} />

                {/* Subtle top gradient accent */}
                <div className={`absolute top-0 left-0 right-0 h-px bg-gradient-to-l ${colors.accent} opacity-20`} />

                {/* Remove button */}
                {onRemove && (
                    <button
                        type="button"
                        onClick={e => { e.stopPropagation(); onRemove(zone.zone_id); }}
                        className="absolute top-2 left-2 z-20 bg-white/80 backdrop-blur-sm rounded-full p-1.5 hover:bg-danger-100 hover:scale-110 active:scale-90 transition-all duration-200 shadow-sm"
                        title="הסר קהילה"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5" className="text-ink-subtle hover:text-danger-500">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                )}

                <div className="relative z-10 flex items-center gap-3">
                    {/* Icon */}
                    <div className="relative flex-shrink-0">
                        <div className={`w-12 h-12 bg-gradient-to-br ${colors.icon} rounded-xl flex items-center justify-center shadow-md ${colors.iconHover} group-hover:shadow-lg transition-all duration-200 group-hover:scale-105`}>
                            <MapPin className="w-5 h-5 text-white drop-shadow-md group-hover:scale-110 transition-transform duration-200" strokeWidth={2.5} />
                        </div>
                        {isMember && (
                            <div className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-gradient-to-br from-success-500 to-success-600 rounded-full border-2 border-white shadow-md animate-pulse" />
                        )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0 flex items-center justify-between gap-3">
                        <div className="flex-1 min-w-0">
                            <h4 className={`font-bold text-ink-strong text-base truncate ${colors.titleHover} transition-colors duration-200`}>
                                {zone.zone_display_name || zone.zone_id}
                            </h4>
                            <div className="flex items-center gap-3 mt-1">
                                {/* Member count */}
                                <div className={`flex items-center gap-1 px-2 py-0.5 bg-gradient-to-br ${colors.badge} rounded-md text-xs ${colors.badgeText} border`}>
                                    <Users className={`w-3.5 h-3.5 ${colors.badgeIcon}`} strokeWidth={2.5} />
                                    <span className="font-bold">{memberCount || 0}</span>
                                    <span className="font-medium">חברים</span>
                                </div>

                                {/* Member avatars */}
                                {members && members.length > 0 && (
                                    <div className="flex items-center gap-1.5">
                                        <div className="flex -space-x-1.5">
                                            {members.slice(0, 3).map((member, idx) => (
                                                <div
                                                    key={idx}
                                                    className={`w-6 h-6 rounded-full bg-gradient-to-br ${colors.avatarBg} border-2 border-white flex items-center justify-center shadow-md hover:scale-125 hover:z-10 transition-transform duration-200`}
                                                    title={member.name || member.user_id}
                                                >
                                                    {member.profile_image ? (
                                                        <img
                                                            src={member.profile_image}
                                                            alt={member.name}
                                                            className="w-full h-full rounded-full object-cover"
                                                        />
                                                    ) : (
                                                        <span className={`text-[10px] font-bold ${colors.avatarText}`}>
                                                            {(member.name || member.user_id).charAt(0).toUpperCase()}
                                                        </span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                        {memberCount > 3 && (
                                            <span className={`text-[10px] font-bold ${colors.counter} px-1.5 py-0.5 rounded border`}>
                                                +{memberCount - 3}
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Arrow */}
                        <ChevronLeft className={`w-4 h-4 ${colors.arrow} flex-shrink-0 transform group-hover:translate-x-[-2px] transition-all duration-200`} strokeWidth={2.5} />
                    </div>
                </div>
            </div>
        </motion.div>
    );
});

// Zones Grid Layout
const ZonesLayout = memo(({ zones, onZoneClick }) => (
    <div className="space-y-2">
        {zones.map((zone, idx) => (
            <ZoneCard
                key={zone.zone_id || idx}
                zone={zone}
                index={idx}
                onClick={() => onZoneClick(zone)}
                onRemove={zone.onRemove}
                memberCount={zone.member_count}
                members={zone.members}
                isMember={zone.is_member}
            />
        ))}
    </div>
));

export const UserZones = ({ chatId }) => {
    const [userZones, setUserZones] = useState([]);
    const [allZones, setAllZones] = useState([]);
    const [editing, setEditing] = useState(false);
    const [selectedZones, setSelectedZones] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const isGuest = chatId === 'null' || chatId === 'guest';
    const [zoneSearch, setZoneSearch] = useState('');
    const [userLocation] = useState(null);
    const [zonesData, setZonesData] = useState({});
    // Content sheet starts expanded over the map (full view, like before); the
    // floating toggle collapses it to reveal the immersive full-page map.
    // Guests land on the immersive map view with the panel collapsed.
    const [panelExpanded, setPanelExpanded] = useState(!isGuest);

    // Scroll to top
    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }, []);

    useEffect(() => {
        const fetchZones = async () => {
            try {
                const res = await cacheSafeFetch(
                    `https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/user-locals?chat_id=${chatId}`,
                    chatId,
                    { method: 'GET' },
                    { cacheKey: `user-locals-${chatId}` }
                );

                const data = await res.json();
                const allZonesData = data.all_zones || [];
                let userLocalsIds = data.user_locals || [];

                if (isGuest) {
                    try {
                        const stored = localStorage.getItem('guest_user_zones');
                        if (stored) userLocalsIds = JSON.parse(stored);
                    } catch { /* ignore parse errors */ }
                }

                const userZonesObjects = userLocalsIds.map(zoneId => {
                    const zoneObj = allZonesData.find(z => z.zone_id === zoneId);
                    return zoneObj || { zone_id: zoneId };
                });

                setUserZones(userZonesObjects);
                setAllZones(allZonesData);
                setSelectedZones(userLocalsIds);
            } catch (err) {
                console.error(err);
                setError('שגיאה בטעינת המקומיות');
            } finally {
                setLoading(false);
            }
        };

        fetchZones();
    }, [chatId, isGuest]);

    // Fetch member data
    useEffect(() => {
        const fetchZonesData = async () => {
            if (!userZones || userZones.length === 0) return;

            try {
                const promises = userZones.map(async (zone) => {
                    const zoneId = typeof zone === 'string' ? zone : zone.zone_id;
                    try {
                        const response = await safeFetch(
                            `https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/zone-members?zone_id=${zoneId}&chat_id=${chatId}`,
                            chatId
                        );

                        if (response.ok) {
                            const data = await response.json();
                            const members = data.members || [];
                            return {
                                zone_id: zoneId,
                                member_count: data.member_count || members.length,
                                members: members,
                                is_member: data.is_current_user_member || false
                            };
                        }
                        return { zone_id: zoneId, member_count: 0, members: [], is_member: false };
                    } catch {
                        return { zone_id: zoneId, member_count: 0, members: [], is_member: false };
                    }
                });

                const results = await Promise.all(promises);
                const dataMap = {};
                results.forEach(result => { dataMap[result.zone_id] = result; });
                setZonesData(dataMap);
            } catch (err) {
                console.error('Error fetching zones data:', err);
            }
        };

        fetchZonesData();
    }, [userZones, chatId]);

    // Markers for the immersive map — every zone, so the whole network is visible.
    const allZoneMarkers = useMemo(() => {
        return allZones
            .map((zone) => {
                if (!zone.location) return null;
                try {
                    const parsed = JSON.parse(zone.location);
                    // Hebrew label for display; zone_id is what the route needs.
                    const name = zone.zone_display_name || zone.zone_id;
                    const id = zone.zone_id;
                    if (Array.isArray(parsed) && parsed.length > 0 && parsed[0]) {
                        return { name, id, lat: parsed[0].latitude, lng: parsed[0].longitude };
                    } else if (parsed && parsed.latitude && parsed.longitude) {
                        return { name, id, lat: parsed.latitude, lng: parsed.longitude };
                    }
                } catch { return null; }
                return null;
            })
            .filter(Boolean);
    }, [allZones]);

    const handleSubmit = async () => {
        if (chatId === 'null' || chatId === 'guest') {
            const selectedZoneObjects = selectedZones.map(zoneId => {
                const zoneObj = allZones.find(z => z.zone_id === zoneId);
                return zoneObj || { zone_id: zoneId };
            });
            setUserZones(selectedZoneObjects);
            try { localStorage.setItem('guest_user_zones', JSON.stringify(selectedZones)); } catch { /* storage unavailable */ }
            setEditing(false);
            return;
        }
        try {
            await safeFetch(`https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/update-user-locals`, chatId, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ chat_id: chatId, zones: selectedZones }),
            });
            const res = await cacheSafeFetch(
                `https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/user-locals?chat_id=${chatId}`,
                chatId,
                { method: 'GET' },
                { cacheKey: `user-locals-${chatId}`, forceRefresh: true }
            );

            const data = await res.json();
            const allZonesData = data.all_zones || [];
            const userLocalsIds = data.user_locals || [];

            const userZonesObjects = userLocalsIds.map(zoneId => {
                const zoneObj = allZonesData.find(z => z.zone_id === zoneId);
                return zoneObj || { zone_id: zoneId };
            });

            setUserZones(userZonesObjects);
            setEditing(false);
        } catch (err) {
            console.error(err);
        }
    };

    const toggleZone = (zone) => {
        setSelectedZones((prev) => (prev.includes(zone) ? prev.filter((z) => z !== zone) : [...prev, zone]));
    };

    const enrichedZones = useMemo(() => userZones.map(z => {
        const zoneObj = typeof z === 'string' ? { zone_id: z } : z;
        const zoneId = zoneObj.zone_id;
        const zoneData = zonesData[zoneId] || { member_count: 0, members: [], is_member: false };
        return {
            ...zoneObj,
            member_count: zoneData.member_count,
            members: zoneData.members,
            is_member: zoneData.is_member,
            onRemove: async (zoneId) => {
                setUserZones(prev => prev.filter(uz => (typeof uz === 'string' ? uz : uz.zone_id) !== zoneId));
                setSelectedZones(prev => prev.filter(sz => sz !== zoneId));
                try {
                    const updatedZones = userZones
                        .filter(uz => (typeof uz === 'string' ? uz : uz.zone_id) !== zoneId)
                        .map(uz => (typeof uz === 'string' ? uz : uz.zone_id));
                    if (isGuest) {
                        try { localStorage.setItem('guest_user_zones', JSON.stringify(updatedZones)); } catch { /* storage unavailable */ }
                    } else {
                        await safeFetch(`https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/update-user-locals`, chatId, {
                            method: 'PUT',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ chat_id: chatId, zones: updatedZones }),
                        });
                    }
                } catch (err) {
                    console.error('Failed to update zones after removal', err);
                }
            }
        };
    }), [userZones, zonesData, chatId]);

    const sortedZones = useMemo(() => [...allZones].sort((a, b) => a.zone_id.localeCompare(b.zone_id, 'he')), [allZones]);
    const filteredZones = useMemo(() => sortedZones.filter(zone => zone.zone_id.toLowerCase().includes(zoneSearch.toLowerCase())), [sortedZones, zoneSearch]);

    if (loading) return <></>;
    if (error) return <p className="text-danger-500 text-center">{error}</p>;

    const hasZones = userZones.length > 0;

    return (
        <div className="relative" style={{ minHeight: '100dvh', background: 'var(--color-bg-base)' }}>
            {/* Full-page immersive map — fixed background spanning the viewport,
                same treatment as the zone-details page. */}
            <div className="fixed inset-0 z-0">
                <ZoneMap zones={allZoneMarkers} chatId={chatId} userLocation={userLocation} />
            </div>

            {/* Soft gradient overlay so the floating chrome stays legible. */}
            <div
                aria-hidden="true"
                className="fixed inset-x-0 top-0 h-40 z-10 pointer-events-none"
                style={{ background: 'linear-gradient(180deg, rgba(245,247,239,0.92) 0%, rgba(245,247,239,0) 100%)' }}
            />

            {/* Floating toolbar — zone identity on the leading (right) edge,
                add-zones + sheet toggle on the trailing edge, clear of the navbar. */}
            <div className="fixed top-[4.25rem] sm:top-[4.75rem] right-4 sm:right-6 left-4 sm:left-6 z-floating flex items-start justify-between gap-2 pointer-events-none">
                <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                    className="pointer-events-auto inline-flex items-center gap-3 bg-white/65 backdrop-blur-xl backdrop-saturate-150 ring-1 ring-white/70 shadow-card rounded-pill pr-3 pl-4 py-2"
                >
                    <span className="relative inline-flex items-center justify-center w-9 h-9 rounded-pill bg-gradient-to-br from-brand-500 to-brand-700 text-white shadow-md ring-2 ring-white/90 shrink-0">
                        <Users className="w-4 h-4" strokeWidth={2.5} />
                    </span>
                    <div className="text-right leading-tight">
                        <h1 className="font-bold text-ink-strong text-sm">הקהילות שלי</h1>
                        <p className="text-xs text-ink-muted">
                            {enrichedZones.length} {enrichedZones.length === 1 ? 'קהילה' : 'קהילות'}
                        </p>
                    </div>
                </motion.div>

                <div className="pointer-events-auto flex items-center gap-2">
                    <button
                        type="button"
                        onClick={() => setEditing(true)}
                        title="הוספת קהילות"
                        className="shrink-0 inline-flex items-center gap-2 h-11 px-4 rounded-pill font-semibold text-sm text-white bg-brand-500 hover:bg-brand-600 shadow-card transition-colors"
                    >
                        <Plus className="w-4 h-4" strokeWidth={2.5} />
                        <span className="hidden sm:inline">הוספה</span>
                    </button>
                    <button
                        type="button"
                        onClick={() => setPanelExpanded(v => !v)}
                        aria-expanded={panelExpanded}
                        title={panelExpanded ? 'תצוגת מפה מלאה' : 'הצגת הקהילות'}
                        className="shrink-0 inline-flex items-center justify-center w-11 h-11 rounded-pill text-ink-muted hover:text-ink bg-white/65 backdrop-blur-xl backdrop-saturate-150 ring-1 ring-white/70 shadow-card transition-colors"
                    >
                        {panelExpanded ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
                    </button>
                </div>
            </div>

            {/* Content sheet — slides up over the map, scrolls internally, and
                leaves the top of the map visible (same pattern as zone-details). */}
            <AnimatePresence>
                {panelExpanded && (
                    <motion.div
                        key="zones-content-panel"
                        initial={{ opacity: 0, y: 48 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 48 }}
                        transition={{ duration: 0.42, ease: [0.22, 1, 0.36, 1] }}
                        className="fixed inset-x-0 bottom-0 z-30 overflow-y-auto overscroll-contain"
                        style={{ top: 'clamp(11rem, 26vh, 16rem)' }}
                    >
                        <div className="relative max-w-3xl mx-auto px-3 sm:px-5 pb-28">
                            <section
                                aria-label="הקהילות שלי"
                                className="relative bg-surface rounded-t-[2rem] sm:rounded-t-[2.5rem] rounded-b-2xl ring-1 ring-line-subtle shadow-elevated"
                            >
                                {/* Decorative top-cap — matches zone-details. */}
                                <div aria-hidden="true" className="absolute inset-x-0 top-0 h-32 rounded-t-[2rem] sm:rounded-t-[2.5rem] overflow-hidden pointer-events-none">
                                    <div className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-brand-400 via-brand-500 to-brand-600" />
                                    <div className="absolute -top-10 -start-12 w-44 h-44 rounded-pill blur-3xl opacity-50 bg-brand-200/70" />
                                    <div className="absolute -top-6 -end-12 w-36 h-36 rounded-pill blur-3xl opacity-30 bg-secondary-200/60" />
                                </div>

                                <div className="relative px-4 sm:px-7 lg:px-9 pt-10 sm:pt-12 pb-8 sm:pb-10">
                                    {/* Grab handle */}
                                    <div aria-hidden="true" className="absolute top-3 left-1/2 -translate-x-1/2 w-10 h-1.5 rounded-full bg-line" />

                                    {!hasZones && !editing && (
                                        <motion.div
                                            initial={{ opacity: 0, scale: 0.95 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            className="flex flex-col items-center justify-center text-center px-4 py-10 max-w-md mx-auto"
                                        >
                                            <div className="w-16 h-16 bg-brand-100 rounded-2xl flex items-center justify-center mb-4">
                                                <MapPin size={32} className="text-brand-500" strokeWidth={2} />
                                            </div>
                                            <h3 className="text-xl font-semibold text-ink-strong mb-2">אין לך עדיין קהילות</h3>
                                            <p className="text-ink-subtle mb-6">
                                                הוסף קהילות כדי לגלות עסקים ואירועים באזורים שחשובים לך
                                            </p>
                                            <button
                                                onClick={() => setEditing(true)}
                                                className="px-6 py-3 text-white font-medium rounded-xl bg-brand-500 hover:bg-brand-600 shadow-sm hover:shadow transition-all"
                                            >
                                                <span className="flex items-center gap-2">
                                                    <Plus className="w-5 h-5" strokeWidth={2} />
                                                    הוסף קהילות
                                                </span>
                                            </button>
                                        </motion.div>
                                    )}

                                    {hasZones && (
                                        <ZonesLayout
                                            zones={enrichedZones}
                                            onZoneClick={zone => navigate('zone-details', `zone=${encodeURIComponent(zone.zone_id)}`)}
                                        />
                                    )}
                                </div>
                            </section>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

                {/* Modal */}
                <AnimatePresence>
                    {editing && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/50"
                            onClick={() => setEditing(false)}
                        >
                            <motion.div
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.95 }}
                                className="bg-white rounded-xl p-5 w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col shadow-xl"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-lg font-semibold text-ink-strong">בחר קהילות</h3>
                                    <button
                                        onClick={() => setEditing(false)}
                                        className="w-8 h-8 rounded-lg bg-surface-muted flex items-center justify-center hover:bg-surface-sunken transition-colors"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" className="text-ink-subtle">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>

                                <div className="mb-3 relative">
                                    <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-subtle" />
                                    <input
                                        type="text"
                                        value={zoneSearch}
                                        onChange={e => setZoneSearch(e.target.value)}
                                        placeholder="חפש קהילה..."
                                        className="w-full pr-10 pl-4 py-2.5 rounded-lg border border-line bg-surface-muted text-ink-strong focus:outline-none focus:border-brand-500 focus:bg-white transition-all"
                                    />
                                </div>

                                <div className="flex-1 overflow-y-auto space-y-1 mb-4">
                                    {filteredZones.map((zone, idx) => (
                                        <label
                                            key={idx}
                                            className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-surface-muted cursor-pointer transition-colors"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedZones.includes(zone.zone_id)}
                                                onChange={() => toggleZone(zone.zone_id)}
                                                className="w-4 h-4 rounded border-line-strong text-brand-500 focus:ring-brand-500"
                                            />
                                            <div className="flex items-center gap-2 flex-1">
                                                <div className="w-7 h-7 bg-brand-500 rounded-lg flex items-center justify-center">
                                                    <MapPin size={14} className="text-white" strokeWidth={2} />
                                                </div>
                                                <span className="font-medium text-ink-strong text-sm">
                                                    {zone.zone_display_name || zone.zone_id}
                                                </span>
                                            </div>
                                        </label>
                                    ))}
                                </div>

                                <div className="flex gap-2">
                                    <button
                                        onClick={handleSubmit}
                                        className="flex-1 text-white font-medium py-2.5 rounded-lg bg-brand-500 hover:bg-brand-600 transition-colors"
                                    >
                                        שמור
                                    </button>
                                    <button
                                        onClick={() => setEditing(false)}
                                        className="px-5 font-medium py-2.5 rounded-lg bg-surface-muted text-ink hover:bg-surface-sunken transition-colors"
                                    >
                                        ביטול
                                    </button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
        </div>
    );
};
